import { endOfMonth, endOfQuarter, format, startOfMonth, startOfQuarter } from 'date-fns'

import { BankTransactionSortProperty } from '@features/bankTransactions/enums/bankTransactionSortProperty'
import { BankLinesQueryParams } from '@features/bankTransactions/query-api'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { PeriodMode } from '../../../../../enums/periodMode'
import { SortDirection } from '../../../../../enums/sortDirection'

const toDateKey = (date: Date): string => format(date, 'yyyy-MM-dd')

interface DateRange {
  from?: string
  through?: string
}

const getPeriodDateRange = (entryDatePeriod: string): DateRange | undefined => {
  const [mode, value] = entryDatePeriod.split(':')

  switch (mode) {
    case PeriodMode.CustomDateFrom:
      return { from: value }
    case PeriodMode.CustomDateThrough:
      return { through: value }
    case PeriodMode.CustomDate: {
      const [from, through] = value.split('...')
      return { from: from || undefined, through: through || undefined }
    }
    case PeriodMode.Month: {
      const [yearString, monthString] = value.split('-')
      const date = new Date(Number(yearString), Number(monthString) - 1, 1)
      return { from: toDateKey(startOfMonth(date)), through: toDateKey(endOfMonth(date)) }
    }
    case PeriodMode.Quarter: {
      const [yearString, quarterString] = value.split('-')
      const date = new Date(Number(yearString), (Number(quarterString) - 1) * 3, 1)
      return { from: toDateKey(startOfQuarter(date)), through: toDateKey(endOfQuarter(date)) }
    }
    case PeriodMode.FiscalYear: {
      const year = value.split(',')[1]
      return { from: `${year}-01-01`, through: `${year}-12-31` }
    }
    default:
      return undefined
  }
}

const compareBankLines = (a: BankLine, b: BankLine, sortProperty: BankTransactionSortProperty): number => {
  if (sortProperty === BankTransactionSortProperty.Amount) {
    return a.amount - b.amount
  }

  return a.entryDate.localeCompare(b.entryDate)
}

export const selectSandboxBankLines = (bankLines: BankLine[], params: BankLinesQueryParams): BankLine[] => {
  const filtered = bankLines.filter((bankLine) => {
    const isReconciled = bankLine.match?.isApproved === true

    if (params.isReconciled !== undefined && isReconciled !== params.isReconciled) {
      return false
    }
    if (params.status !== undefined && bankLine.status !== params.status) {
      return false
    }
    if (params.side !== undefined && bankLine.side !== params.side) {
      return false
    }
    if (params.minAmount !== undefined && bankLine.amount < params.minAmount) {
      return false
    }
    if (params.maxAmount !== undefined && bankLine.amount > params.maxAmount) {
      return false
    }
    if (params.q && !bankLine.description.toLowerCase().includes(params.q.toLowerCase())) {
      return false
    }

    if (params.entryDatePeriod && params.entryDatePeriod !== PeriodMode.All) {
      const range = getPeriodDateRange(params.entryDatePeriod)

      if (range?.from && bankLine.entryDate < range.from) {
        return false
      }
      if (range?.through && bankLine.entryDate > range.through) {
        return false
      }
    }

    return true
  })

  if (params.sortProperty) {
    const direction = params.sortDirection === SortDirection.Desc ? -1 : 1
    filtered.sort((a, b) => compareBankLines(a, b, params.sortProperty as BankTransactionSortProperty) * direction)
  }

  return filtered
}
